/** Who may see a session, and the link to hand them.
 *
 * Three choices, saved the moment one is picked: private, anyone with the
 * link, or public. Each says in a sentence who can open the drive and what
 * becomes of links already sent, because the difference between the last two
 * — listed for other drivers or not — is the whole point of having both.
 *
 * Once the session is shared the dialog shows what the people it is sent to
 * will see first: the preview card a link unfurls into on X, Reddit, iMessage
 * and the rest, drawn by the service from the session as it is now. Under it
 * is the link to copy, the two sites' compose screens with the link and a
 * line about the drive filled in, and the device's own share sheet where
 * there is one (a phone's, which is how a link reaches a group chat). The
 * card prints top speed in one unit for everybody, so the unit it uses is
 * offered here too, starting from the driver's own.
 *
 * The focus handling is the portal's other dialog's (`ConfirmDialog`): focus
 * moves in on open and back on close, Escape closes, Tab stays inside.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { CopyButton, ErrorNote, Segmented, Spinner } from '@/components/ui';
import { patchSession, shareCardUrl, shareUrl } from '@/lib/api';
import type { DriveSession, SessionVisibility } from '@/lib/types';
import type { SpeedUnit } from '@/lib/units';
import { speedLabel } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';
import {
  VISIBILITY_CHOICES,
  postText,
  redditPostUrl,
  visibilityOf,
  xPostUrl,
} from '@/components/sessions/share';

export interface ShareDialogProps {
  session: DriveSession;
  /** The session's fastest lap, for the line the post starts with. */
  bestLapMs: number | null;
  /** The session as the server now has it, after any change. */
  onChanged: (session: DriveSession) => void;
  onClose: () => void;
}

function focusable(root: HTMLElement): HTMLElement[] {
  const found = root.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  return Array.from(found);
}

export function ShareDialog({ session, bestLapMs, onChanged, onClose }: ShareDialogProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const chosenRadio = useRef<HTMLButtonElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const { unit } = useSpeedUnit();

  const [saving, setSaving] = useState<SessionVisibility | 'unit' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loadedCard, setLoadedCard] = useState<string | null>(null);

  const visibility = visibilityOf(session);
  const token = visibility === 'private' ? null : (session.share_token ?? null);
  const url = token ? shareUrl(token) : '';
  const text = postText(session, bestLapMs);
  const cardUnit: SpeedUnit = session.share_unit ?? unit;

  useEffect(() => {
    // Focus starts on the choice that is in force, as a radio group's does,
    // and at the top of a dialog that may be taller than the screen.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    chosenRadio.current?.focus();
    return () => opener?.focus();
  }, []);

  // A new card is drawn whenever what it shows changes. The address names
  // those things so this browser does not show the copy it kept from before
  // a lap arrived or the unit changed; the frame keeps its size meanwhile.
  const cardSrc = token
    ? `${shareCardUrl(token)}?v=${encodeURIComponent(`${session.laps}-${session.best_lap_ms ?? 0}-${cardUnit}`)}`
    : null;
  // Which picture has loaded, not whether one has: a picture this browser
  // already holds can load before any effect here runs.
  const cardLoaded = cardSrc !== null && loadedCard === cardSrc;

  const save = useCallback(
    async (change: { visibility?: SessionVisibility; share_unit?: SpeedUnit }, what: SessionVisibility | 'unit') => {
      setSaving(what);
      setError(null);
      try {
        const reply = await patchSession(session.id, {
          ...change,
          // Sharing for the first time prints the driver's own unit.
          ...(change.visibility && change.visibility !== 'private' && !session.share_token
            ? { share_unit: unit }
            : {}),
        });
        onChanged(reply.session);
      } catch (failure) {
        setError(failure);
      } finally {
        setSaving(null);
      }
    },
    [session.id, session.share_token, unit, onChanged],
  );

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close.current();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;
    const stops = focusable(panel.current);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div
      className="dialog-backdrop"
      onKeyDown={onKeyDown}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="dialog"
        style={{ width: 'min(560px, 100%)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}
      >
        <h2 className="dialog-title" id={titleId}>
          Share this session
        </h2>

        <div role="radiogroup" aria-labelledby={titleId} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {VISIBILITY_CHOICES.map((choice) => {
            const chosen = choice.value === visibility;
            return (
              <button
                key={choice.value}
                ref={chosen ? chosenRadio : undefined}
                type="button"
                role="radio"
                aria-checked={chosen}
                aria-label={choice.label}
                aria-describedby={`${titleId}-${choice.value}`}
                disabled={saving !== null}
                onClick={() => {
                  if (!chosen) void save({ visibility: choice.value }, choice.value);
                }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '18px minmax(0, 1fr)',
                  gap: 10,
                  alignItems: 'start',
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${chosen ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                  background: chosen ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)' : 'transparent',
                  color: 'inherit',
                  cursor: chosen ? 'default' : 'pointer',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    marginTop: 2,
                    borderRadius: '50%',
                    border: `2px solid ${chosen ? 'var(--color-accent)' : 'var(--color-neutral-500)'}`,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {saving === choice.value ? (
                    <Spinner size={10} />
                  ) : chosen ? (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent)' }} />
                  ) : null}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{choice.label}</span>
                  <span id={`${titleId}-${choice.value}`} style={{ fontSize: 12.5, opacity: 0.75, textWrap: 'pretty' }}>
                    {choice.detail}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {error ? <ErrorNote error={error} compact title="could not change who can see this session" /> : null}

        {token ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* What a link unfurls into, drawn by the service. */}
            <div
              style={{
                position: 'relative',
                aspectRatio: '1200 / 630',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: '#141625',
                border: '1px solid var(--color-divider)',
              }}
            >
              {cardSrc ? (
                <img
                  src={cardSrc}
                  alt="The preview a link to this session shows when it is posted"
                  width={1200}
                  height={630}
                  onLoad={() => setLoadedCard(cardSrc)}
                  style={{ width: '100%', height: '100%', display: 'block', opacity: cardLoaded ? 1 : 0, transition: 'opacity 160ms' }}
                />
              ) : null}
              {!cardLoaded ? (
                <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                  <Spinner size={18} />
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="input"
                readOnly
                value={url}
                aria-label="Share link"
                onFocus={(event) => event.currentTarget.select()}
                style={{ flex: 1, minWidth: 0, fontSize: 13 }}
              />
              <CopyButton text={url} label="Copy link" variant="primary" />
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <a className="btn btn-secondary" href={xPostUrl(url, text)} target="_blank" rel="noopener noreferrer">
                Post to X
              </a>
              <a
                className="btn btn-secondary"
                href={redditPostUrl(url, text)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Post to Reddit
              </a>
              {canNativeShare ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    // Dismissing the sheet rejects; that is not an error.
                    navigator.share({ title: text, text, url }).catch(() => undefined);
                  }}
                >
                  More…
                </button>
              ) : null}
              <span style={{ flex: 1 }} />
              <Segmented<SpeedUnit>
                label="Top speed on the card"
                value={cardUnit}
                onChange={(next) => {
                  if (next !== session.share_unit) void save({ share_unit: next }, 'unit');
                }}
                options={[
                  { value: 'kmh', label: speedLabel('kmh') },
                  { value: 'mph', label: speedLabel('mph') },
                ]}
              />
            </div>

            <p style={{ margin: 0, fontSize: 12, opacity: 0.7, textWrap: 'pretty' }}>
              The page shows your display name and every lap of this drive. A shared session keeps every lap&rsquo;s
              recording for as long as it is shared.
            </p>
          </div>
        ) : null}

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShareDialog;
